<template>
  <ElDialog
      v-model="visible"
      :footer="null"
      append-to-body
      align-center
      :show-close="true"
      :close-on-click-modal="false"
      :width="620"
      destroyOnClose
  >
    <template #header>
      <span class="el-dialog__title">{{ t('invite_friend.title') }} - {{ t('invite_friend.free_get') }}</span>
      <div class="dialog-header-divider"></div>
    </template>
    <div class="invite-content">
      <div class="row">
        <div class="label" style="width: 100%;">{{ t('invite_friend.resource') }} - {{ t('invite_friend.resource_desc') }}</div>
        <ElSelect v-model="form.resource" class="resource-select" :placeholder="t('invite_friend.select_resource')" clearable>
          <template #label="{ label }">
            <span>{{ getSelectedResourceLabelParts(label).prefix }}</span>
            <strong v-if="getSelectedResourceLabelParts(label).qty">{{ getSelectedResourceLabelParts(label).qty }}</strong>
          </template>
          <ElOption
              v-for="opt in resourceOptions"
              :key="String(opt.value)"
              :label="formatResourceLabel(opt)"
              :value="opt.value"
          >
            <template #default>
              <span>{{ getResourceLabelParts(opt).prefix }}</span>
              <strong v-if="getResourceLabelParts(opt).qty">{{ getResourceLabelParts(opt).qty }}</strong>
            </template>
          </ElOption>
        </ElSelect>
      </div>
      <div style="margin: 10px 0px;">{{ t('invite_friend.monthly_left_prefix') }}<span class="tips">{{ inviteChancesLeft }}</span>{{ t('invite_friend.monthly_left_suffix') }}</div>
      <div class="desc">
        {{ t('invite_friend.desc') }}
      </div>
      <div class="desc" style="margin:10px 0px;color: #4982f7;">
        {{ t('invite_friend.how_to_invite') }}
      </div>
      <div>
        <div class="invite-steps">
          <div class="invite-step" v-for="(s, idx) in inviteSteps" :key="idx">
            <div class="invite-step__icon">
              <img class="invite-step__icon-img" :src="s.icon" alt="" />
            </div>
            <div class="invite-step__text">
              <div class="invite-step__title">{{ s.title }}</div>
              <div class="invite-step__desc">{{ s.desc }}</div>
            </div>
            <div v-if="idx !== inviteSteps.length - 1" class="invite-step__arrow">›</div>
          </div>
        </div>
      </div>
      <!-- <div class="rules">
        <div>{{ t('invite_friend.rule_1') }}</div>
        <div>{{ t('invite_friend.rule_2', { firstYear: INVITE_MONTHLY_QUOTA_FIRST_YEAR, afterFirstYear: INVITE_MONTHLY_QUOTA_AFTER_FIRST_YEAR }) }}</div>
        <div>{{ t('invite_friend.rule_3') }}</div>
      </div> -->
      <div class="actions">
        <ElButton type="danger" @click="handleGenerate" :loading="loading" :disabled="!canGenerate">{{ t('invite_friend.generate_link') }}</ElButton>
      </div>
     <div class="actions" style="margin-top: 10px;">
       <el-button type="primary" link style="text-decoration: underline;" @click="handleGetResourceDetail">{{ t('invite_friend.get_resource_detail') }}</el-button>
    </div>
     </div>
  </ElDialog>

  <ElDialog
      v-model="linkDialogVisible"
      :footer="null"
      append-to-body
      align-center
      :show-close="true"
      :close-on-click-modal="false"
      :width="540"
      destroyOnClose
  >
    <template #header>
      <span class="el-dialog__title">{{ t('invite_friend.title') }}</span>
      <div class="dialog-header-divider"></div>
    </template>
    <div class="invite-link-dialog">
      <div class="invite-link-label">{{ t('invite_friend.invite_link') }}</div>
      <ElInput
          v-model="inviteLink"
          type="textarea"
          :autosize="{ minRows: 3, maxRows: 6 }"
          readonly
          class="invite-link-input"
      />
      <div class="invite-link-actions">
        <ElButton type="danger" @click="handleCopy" :disabled="!inviteLink">{{ t('invite_friend.copy_link') }}</ElButton>
        <ElButton type="primary" @click="handleSendEmail" :loading="emailSending" :disabled="!inviteLink">{{ t('invite_friend.send_email') }}</ElButton>
      </div>
    </div>
  </ElDialog>

  <ResourceDetailDialog v-model="resourceDetailVisible" :use-invite-list="true" />
</template>

<script setup>
import {computed, ref, watch} from 'vue'
import {ElButton, ElDialog, ElInput, ElMessage, ElOption, ElSelect} from 'element-plus'
import { useI18n } from 'vue-i18n'
import {useRouter} from 'vue-router'
import ResourceDetailDialog from '@/components/ResourceDetailDialog/index.vue'
import {getInviteNumber} from '@/api/index'
import {INVITE_MONTHLY_QUOTA_AFTER_FIRST_YEAR, INVITE_MONTHLY_QUOTA_FIRST_YEAR} from '@/config/invite'
import step1Icon from '@/assets/user_friend_step1.svg'
import step2Icon from '@/assets/user_friend_step2.svg'
import step3Icon from '@/assets/user_friend_step3.svg'

const { t } = useI18n()

const inviteSteps = computed(() => [
  {
    title: t('invite_friend.step1_title'),
    desc: t('invite_friend.step1_desc'),
    icon: step1Icon,
  },
  {
    title: t('invite_friend.step2_title'),
    desc: t('invite_friend.step2_desc'),
    icon: step2Icon,
  },
  {
    title: t('invite_friend.step3_title'),
    desc: t('invite_friend.step3_desc'),
    icon: step3Icon,
  },
])

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  userInfo: {
    type: Object,
    default: null
  },
  resourceOptions: {
    type: Array,
    default: () => []
  },
  defaultResource: {
    type: [String, Number],
    default: ''
  },
  chancesLeft: {
    type: Number,
    default: null
  },
  onGenerate: {
    type: Function,
    default: null
  },
  onSendEmail: {
    type: Function,
    default: null
  }
})

const emit = defineEmits(['update:modelValue', 'generate', 'send-email'])

const router = useRouter()

const visible = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
})

const resourceOptions = computed(() => {
  return Array.isArray(props.resourceOptions) ? props.resourceOptions : []
})

const getResourceLabelKey = (opt) => {
  // 优先使用 value 或 id 等唯一标识来判断资源类型
  // 如果后端提供了 productSort 字段，使用它
  if (opt?.productSort !== undefined) {
    const sortMap = {
      1: 'seat',
      2: 'storage',
      3: 'email',
      4: 'ai_points'
    }
    return sortMap[opt.productSort] || ''
  }
  
  // 如果有 type 字段
  if (opt?.type) {
    const typeStr = String(opt.type).toLowerCase()
    if (typeStr.includes('seat') || typeStr.includes('workstation')) return 'seat'
    if (typeStr.includes('storage') || typeStr.includes('space')) return 'storage'
    if (typeStr.includes('email') || typeStr.includes('edm')) return 'email'
    if (typeStr.includes('ai') || typeStr.includes('point')) return 'ai_points'
  }
  
  // 回退方案：检测 label 中的关键字（支持中英文）
  const raw = String(opt?.label ?? '').toLowerCase()
  // 检测工位/seat
  if (raw.includes('工位') || raw.includes('seat') || raw.includes('workstation')) return 'seat'
  // 检测存储/storage
  if (raw.includes('存储') || raw.includes('空间') || raw.includes('storage') || raw.includes('space') || raw.includes('gb')) return 'storage'
  // 检测邮件/email
  if (raw.includes('邮件') || raw.includes('email') || raw.includes('edm')) return 'email'
  // 检测点数/AI points
  if (raw.includes('点数') || raw.includes('point') || raw.includes('ai')) return 'ai_points'
  
  return ''
}

const getResourceLabelParts = (opt) => {
  const key = getResourceLabelKey(opt)
  if (!key) {
    const raw = String(opt?.label ?? '')
    return {prefix: raw, qty: ''}
  }
  return {
    prefix: t(`invite_friend.resource_label.${key}_prefix`),
    qty: t(`invite_friend.resource_label.${key}_qty`),
  }
}

const getSelectedResourceLabelParts = (label) => {
  const raw = String(label ?? '')
  const matched = resourceOptions.value.find((o) => String(formatResourceLabel(o)) === raw)
  if (matched) return getResourceLabelParts(matched)
  return {prefix: raw, qty: ''}
}

const formatResourceLabel = (opt) => {
  const {prefix, qty} = getResourceLabelParts(opt)
  return `${prefix}${qty}`
}

const form = ref({
  resource: ''
})

const loading = ref(false)
const inviteLink = ref('')
const linkDialogVisible = ref(false)
const emailSending = ref(false)
const resourceDetailVisible = ref(false)

const invitedCount = ref(0)

const getUserRegisterTs = (info) => {
  if (!info || typeof info !== 'object') return null
  const candidates = [
    info.registerTime,
    info.registerDate,
    info.createTime,
    info.createDate,
    info.gmtCreate,
    info.addTime
  ]
  const raw = candidates.find(v => v !== undefined && v !== null && v !== '')
  if (raw == null) return null
  const ts = typeof raw === 'string' ? new Date(raw).getTime() : Number(raw)
  return Number.isFinite(ts) ? ts : null
}

const monthlyQuota = computed(() => {
  const overrideRaw = props.chancesLeft
  if (overrideRaw !== null && overrideRaw !== undefined && overrideRaw !== '') {
    const override = Number(overrideRaw)
    if (Number.isFinite(override) && override >= 0) return override
  }

  return INVITE_MONTHLY_QUOTA_FIRST_YEAR
})

const inviteChancesLeft = computed(() => {
  const total = Number(monthlyQuota.value)
  const totalSafe = Number.isFinite(total) ? total : 0
  const used = Number(invitedCount.value)
  const usedSafe = Number.isFinite(used) ? used : 0
  return Math.max(0, totalSafe - usedSafe)
})

const canGenerate = computed(() => {
  const n = Number(inviteChancesLeft.value)
  return !loading.value && Number.isFinite(n) && n > 0
})

const fetchInviteNumber = async () => {
  try {
    const res = await getInviteNumber({})
    if (res?.code !== 200) return
    const totalSafe = Number(monthlyQuota.value)
    const raw = res?.result

    // 接口兼容：
    // - 期望接口返回“已使用次数 usedCount”，页面剩余 = quota - usedCount
    // - 若后端改为返回对象：优先识别 usedCount / count / used，其次识别 remainingCount / leftCount
    if (raw && typeof raw === 'object') {
      const usedLike = Number(raw.usedCount ?? raw.count ?? raw.used)
      if (Number.isFinite(usedLike)) {
        invitedCount.value = usedLike
        return
      }

      const remainingLike = Number(raw.remainingCount ?? raw.leftCount ?? raw.remaining)
      if (Number.isFinite(remainingLike) && Number.isFinite(totalSafe)) {
        invitedCount.value = Math.max(0, totalSafe - remainingLike)
      }
      return
    }

    const usedCount = Number(raw)
    if (Number.isFinite(usedCount)) {
      invitedCount.value = usedCount
    }
  } catch (e) {
  }
}

watch(
  () => props.modelValue,
  (val) => {
    if (val) {
      form.value.resource = props.defaultResource === 0 ? 0 : (props.defaultResource || '')
      inviteLink.value = ''
      linkDialogVisible.value = false
      resourceDetailVisible.value = false
      invitedCount.value = 0
      fetchInviteNumber()
    }
  }
)

watch(
    () => props.defaultResource,
    (val) => {
      if (props.modelValue) {
        form.value.resource = val === 0 ? 0 : (val || '')
      }
    }
)

const handleGenerate = async () => {
  if (!canGenerate.value) {
    ElMessage.warning(t('invite_friend.monthly_used_up'))
    return
  }
  loading.value = true
  inviteLink.value = ''
  try {
    const payload = { resource: form.value.resource }
    emit('generate', payload)
    if (typeof props.onGenerate === 'function') {
      const result = await props.onGenerate(payload)
      if (typeof result === 'string') {
        inviteLink.value = result
        linkDialogVisible.value = true
      }
    }
  } finally {
    loading.value = false
    fetchInviteNumber()
  }
}

const handleCopy = async () => {
  if (!inviteLink.value) return
  try {
    await navigator.clipboard.writeText(inviteLink.value)
    ElMessage.success(t('invite_friend.copy_success'))
  } catch (e) {
    ElMessage.error(t('invite_friend.copy_failed'))
  }
}

const handleSendEmail = async () => {
  if (!inviteLink.value) return
  const payload = { link: inviteLink.value, resource: form.value.resource }
  emit('send-email', payload)

  emailSending.value = true
  try {
    const resolved = router.resolve({
      path: '/email/write_mail',
      query: {
        type: 'invite_friend',
        inviteFlag: '1',
        inviteLink: inviteLink.value,
      },
    })
    window.open(resolved.href, '_blank')
    linkDialogVisible.value = false
    visible.value = false
  } finally {
    emailSending.value = false
  }
}

const handleGetResourceDetail = () => {
  resourceDetailVisible.value = true
}
</script>

<style scoped lang="less">
.invite-content {
  padding: 0 20px;
}

.dialog-header-divider {
  border-bottom: 1px solid #f3f3f3;
  margin-top: 10px;
  width: 100%;
}

.row {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
}

.invite-steps {
  display: flex;
  gap: 10px;
  align-items: stretch;
}

.invite-step {
  flex: 1;
  min-width: 0;
  background: #f7faff;
  border: 1px solid #e6efff;
  border-radius: 10px;
  padding: 10px;
  display: flex;
  align-items: center;
  position: relative;
}

.invite-step__icon {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: rgba(22, 119, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  margin-right: 10px;
}

.invite-step__icon-img {
  width: 22px;
  height: 22px;
  display: block;
}

.invite-step__text {
  min-width: 0;
}

.invite-step__title {
  font-size: 13px;
  font-weight: 600;
  color: #1f2329;
  line-height: 18px;
}

.invite-step__desc {
  margin-top: 4px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
  line-height: 16px;
}

.invite-step__arrow {
  flex: 0 0 auto;
  margin-left: 10px;
  color: #1677ff;
  font-size: 18px;
  line-height: 18px;
}

.label {
  width: 48px;
  font-weight: 500;
  font-size: 14px;
  color: #444444;
}

.resource-select {
  flex: 1;
}

.tips {
  margin-top: 10px;
  color: #d93737;
  font-size: 13px;
}

.desc {
  color: #303133;
  font-size: 13px;
}

.rules {
  margin-top: 10px;
  color: #606266;
  font-size: 13px;
  line-height: 20px;
}

.actions {
  display: flex;
  justify-content: center;
  margin-top: 18px;
}

.invite-link-dialog {
  padding: 0 20px 10px;
}

.invite-link-label {
  margin-bottom: 10px;
  color: #303133;
}

.invite-link-input {
  width: 100%;
}

.invite-link-actions {
  margin-top: 18px;
  display: flex;
  justify-content: center;
  gap: 12px;
}
</style>
